import {type ReactNode, useMemo, useState, useEffect, useCallback} from 'react'
import z from 'zod'
import {
    GlobalVesselFilterContext,
    type GlobalVesselContextValue,
} from './global-vessel-filter-context'
import type {GuidType} from '@/values/generic-type-definitions'
import useTypedSelector from '@/hooks/use-typed-selector'
import {currentUserSelector} from '@/store/state/current-user/selectors'
import {useQueryVessels} from '@/query/vessels/vessels'

interface Props {
    children: ReactNode
}

const UserFilterEntrySchema = z.object({
    filter: z.array(z.string()),
    lastInteracted: z.number(),
})

const FilterStorageSchema = z.record(z.string(), UserFilterEntrySchema)

type FilterStorage = Partial<z.infer<typeof FilterStorageSchema>>

const LOCAL_STORAGE_KEY = 'global-vessel-filter'
const MAX_USERS = 2

function readStorage(): FilterStorage {
    try {
        const raw = localStorage.getItem(LOCAL_STORAGE_KEY)
        if (raw) {
            return FilterStorageSchema.parse(JSON.parse(raw))
        }
    } catch (error) {
        console.error('Failed to parse vessel filter from local storage:', error)
    }
    return {}
}

function writeStorage(storage: FilterStorage): void {
    try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(storage))
    } catch (error) {
        console.error('Failed to write vessel filter to local storage:', error)
    }
}

function saveUserFilter(userId: string, vesselIds: Set<GuidType>): void {
    let storage = readStorage()

    const storageVals = Object.keys(storage)
    if (!(userId in storage) && storageVals.length >= MAX_USERS) {
        const oldestKey = storageVals.reduce((a, b) => {
            const prevTimestamp = storage[a]?.lastInteracted ?? 0
            const nextTimestamp = storage[b]?.lastInteracted ?? 0
            return prevTimestamp < nextTimestamp ? a : b
        })
        storage = Object.fromEntries(Object.entries(storage).filter(([key]) => key !== oldestKey))
    }

    storage[userId] = {
        filter: [...vesselIds],
        lastInteracted: Date.now(),
    }

    writeStorage(storage)
}

function loadUserFilter(userId: string): GuidType[] | null {
    const storage = readStorage()
    const entry = storage[userId]
    return entry?.filter ?? null
}

export function GlobalVesselFilterProvider({children}: Props) {
    const {
        data: vesselData = [],
        isLoading,
        error,
    } = useQueryVessels({statuses: ['ACTIVE', 'OTSM_ONLY']})

    const [selectedVesselIds, setSelectedVesselIds] = useState<Set<GuidType> | null>(null)
    const userId = useTypedSelector(currentUserSelector).user
    const [isOpen, setIsOpen] = useState<boolean>(false)

    useEffect(() => {
        // Only load filter preferences from localStorage when vesselData loads + user exists + selectedIds === null (which will always be null on initial load)
        if (vesselData.length > 0 && selectedVesselIds === null && userId) {
            const saved = loadUserFilter(userId)
            setSelectedVesselIds(new Set(saved ?? vesselData.map((v) => v.location)))
        }
    }, [vesselData, selectedVesselIds, userId])

    const onChange = useCallback(
        (newSelectedVesselIds: Set<GuidType>) => {
            setSelectedVesselIds(newSelectedVesselIds)
            if (userId) {
                saveUserFilter(userId, newSelectedVesselIds)
            }
        },
        [userId],
    )

    const allVesselArray = vesselData

    const selectedVesselArray = useMemo(
        () =>
            selectedVesselIds === null
                ? vesselData
                : vesselData.filter((v) => selectedVesselIds.has(v.location)),
        [selectedVesselIds, vesselData],
    )

    const value: GlobalVesselContextValue = useMemo(
        () => ({
            isLoading,
            isOpen,
            setIsOpen,
            allVesselArray,
            selectedVesselIds,
            selectedVesselArray,
            onChange,
            error,
        }),
        [
            allVesselArray,
            isOpen,
            setIsOpen,
            selectedVesselIds,
            selectedVesselArray,
            onChange,
            isLoading,
            error,
        ],
    )

    return (
        <GlobalVesselFilterContext.Provider value={value}>
            {children}
        </GlobalVesselFilterContext.Provider>
    )
}
